/**
 * Score Ético — indicador compuesto para programas de denuncias internas.
 *
 * Emite tres representaciones del mismo cálculo:
 *   score  — 0 a 5, donde 5 es saludable (escala interna)
 *   riesgo — 0 a 100, donde 100 es crítico (escala del gauge)
 *   grade  — A / B / C / D
 *
 * Diseño:
 *   Volumen        campana log-normal asimétrica centrada en el benchmark.
 *                  Castiga el silencio y el exceso por igual.
 *   Sustanciación  campana centrada en ~46%.
 *   Tiempo cierre  decaimiento exponencial con meseta hasta 28 días.
 *   Represalias    decaimiento exponencial monótono, sin meseta.
 *   Composición    media geométrica ponderada: una dimensión colapsada
 *                  arrastra el total, sin compensación de las demás.
 *
 * Benchmarks: NAVEX Whistleblowing & Incident Management 2024/2025.
 */

import {
  CHART_GREEN,
  CHART_ORANGE,
  CHART_RED,
} from "../chartPalette";

// ---------------------------------------------------------------------------
// Benchmarks (denuncias anuales por cada 100 empleados)
// ---------------------------------------------------------------------------

export const BENCHMARKS = {
  global: 1.65,
  norteamerica: 1.75,
  sudamerica: 2.97,
  apac: 0.78,
  europa: 0.67,
  salud: 3.96,
};

// ---------------------------------------------------------------------------
// Parámetros de calibración
// ---------------------------------------------------------------------------

export const PARAMETROS_DEFAULT = {
  // Volumen
  benchmark: 1.65,
  kSuavizado: 50,
  sigmaBajo: 1.0,
  sigmaAlto: 1.4,

  // Sustanciación
  sustObjetivo: 0.46,
  sustSigma: 0.25,

  // Tiempo de cierre
  diasObjetivo: 28,
  lambdaTiempo: 0.022,

  // Represalias
  lambdaRepresalias: 12,

  // Pesos (deben sumar 1)
  pesoVolumen: 0.35,
  pesoSustanciacion: 0.2,
  pesoTiempo: 0.2,
  pesoRepresalias: 0.25,

  // Piso por subscore: un cero no debe anular el producto entero
  piso: 0.1,
};

export function parametrosParaRegion(region, overrides = {}) {
  const clave = String(region).toLowerCase().trim();
  if (!(clave in BENCHMARKS)) {
    throw new Error(
      `Región "${region}" no reconocida. Opciones: ${Object.keys(BENCHMARKS).join(", ")}`
    );
  }
  return { ...PARAMETROS_DEFAULT, benchmark: BENCHMARKS[clave], ...overrides };
}

// ---------------------------------------------------------------------------
// Bandas del gauge — ordenadas de MENOR a MAYOR riesgo.
// Ojo: en el gauge, A queda a la IZQUIERDA (riesgo bajo) y D a la DERECHA.
// ---------------------------------------------------------------------------

// El amarillo de la paleta (CHART_AMBER, #ca8a04) tira a dorado oscuro y
// ensucia la rampa verde→rojo del gauge, así que aquí — y sólo aquí — se usa
// el amarillo original del diseño.
const AMARILLO_GAUGE = "#F5D04E";

export const BANDAS = [
  { grade: "A", min: 0, max: 20, color: CHART_GREEN, etiqueta: "Sano" },
  { grade: "B", min: 20, max: 40, color: AMARILLO_GAUGE, etiqueta: "Bajo" },
  { grade: "C", min: 40, max: 65, color: CHART_ORANGE, etiqueta: "Alto" },
  { grade: "D", min: 65, max: 100, color: CHART_RED, etiqueta: "Crítico" },
];

export function bandaPara(riesgo) {
  return BANDAS.find((b) => riesgo < b.max) ?? BANDAS[BANDAS.length - 1];
}

// ---------------------------------------------------------------------------
// Subscores
// ---------------------------------------------------------------------------

/**
 * Tasa anualizada por 100 empleados con shrinkage bayesiano hacia el
 * benchmark. Evita que una unidad chica con 1 caso se vea catastrófica.
 *
 *   R = (D + k·B/100) / (E + k) × 100 × (12/meses)
 */
export function tasaAjustada(periodo, p = PARAMETROS_DEFAULT) {
  const numerador = periodo.casosUnicos + p.kSuavizado * (p.benchmark / 100);
  const denominador = periodo.empleados + p.kSuavizado;
  return (numerador / denominador) * 100 * (12 / periodo.meses);
}

/**
 * Campana log-normal asimétrica.
 *   d = ln(R / B)
 *   S = 5 · exp(−d² / 2σ²),  σ = sigmaBajo si d<0, sigmaAlto si d≥0
 */
export function scoreVolumen(tasa, p = PARAMETROS_DEFAULT) {
  if (tasa <= 0) return 0;
  const d = Math.log(tasa / p.benchmark);
  const sigma = d < 0 ? p.sigmaBajo : p.sigmaAlto;
  return 5 * Math.exp(-(d * d) / (2 * sigma * sigma));
}

/**
 * Muy baja = canal saturado de quejas triviales.
 * Cercana a 1.0 = solo denuncian con pruebas irrefutables; se pierde
 * la señal preventiva.
 */
export function scoreSustanciacion(tasaSust, p = PARAMETROS_DEFAULT) {
  const d = tasaSust - p.sustObjetivo;
  return 5 * Math.exp(-(d * d) / (2 * p.sustSigma * p.sustSigma));
}

/** Meseta hasta el estándar de 28 días, luego decaimiento. */
export function scoreTiempo(diasMediana, p = PARAMETROS_DEFAULT) {
  const exceso = Math.max(0, diasMediana - p.diasObjetivo);
  return 5 * Math.exp(-p.lambdaTiempo * exceso);
}

/**
 * Monótona y sin meseta. El ~3% de la industria es una mediana
 * descriptiva, no una meta: no se premia ser promedio aquí.
 */
export function scoreRepresalias(tasaRepr, p = PARAMETROS_DEFAULT) {
  return 5 * Math.exp(-p.lambdaRepresalias * tasaRepr);
}

// ---------------------------------------------------------------------------
// Composición
// ---------------------------------------------------------------------------

/** Media geométrica ponderada: Π (Sᵢ ^ wᵢ). Pesos renormalizados. */
export function mediaGeometrica(valores, pesos) {
  const total = pesos.reduce((a, b) => a + b, 0);
  if (total <= 0) throw new Error("La suma de pesos debe ser > 0");
  const acumulado = valores.reduce(
    (acc, v, i) => acc + (pesos[i] / total) * Math.log(v),
    0
  );
  return Math.exp(acumulado);
}

const redondear = (n, d = 2) => Math.round(n * 10 ** d) / 10 ** d;

/**
 * @param {Object} periodo
 * @param {number} periodo.empleados            plantilla declarada (promedio)
 * @param {number} periodo.casosUnicos          incidentes deduplicados
 * @param {number} [periodo.meses=12]
 * @param {number} [periodo.reportesBrutos]     antes de deduplicar
 * @param {number} [periodo.tasaSustanciacion]  0–1
 * @param {number} [periodo.diasMedianaCierre]
 * @param {number} [periodo.casosRepresalias=0]
 * @param {number} [periodo.maxReportesMismoSujeto=0]
 * @param {number} [periodo.denunciantesUnicos] para validar el denominador
 */
export function calcular(periodo, parametros = {}) {
  const p = { ...PARAMETROS_DEFAULT, ...parametros };
  const d = {
    meses: 12,
    casosRepresalias: 0,
    maxReportesMismoSujeto: 0,
    ...periodo,
  };

  if (!(d.empleados > 0)) throw new Error("empleados debe ser > 0");
  if (!(d.casosUnicos >= 0)) throw new Error("casosUnicos no puede ser negativo");
  if (!(d.meses > 0)) throw new Error("meses debe ser > 0");
  if (
    d.tasaSustanciacion != null &&
    (d.tasaSustanciacion < 0 || d.tasaSustanciacion > 1)
  ) {
    throw new Error("tasaSustanciacion debe estar entre 0 y 1");
  }

  const pesosSuma =
    p.pesoVolumen + p.pesoSustanciacion + p.pesoTiempo + p.pesoRepresalias;
  if (Math.abs(pesosSuma - 1) > 1e-9) {
    throw new Error(`Los pesos deben sumar 1.0, suman ${pesosSuma}`);
  }

  const rCruda = (d.casosUnicos / d.empleados) * 100 * (12 / d.meses);
  const rAjustada = tasaAjustada(d, p);

  const subscores = { volumen: scoreVolumen(rAjustada, p) };
  const valores = [subscores.volumen];
  const pesos = [p.pesoVolumen];
  const evaluados = ["volumen"];

  if (d.tasaSustanciacion != null) {
    subscores.sustanciacion = scoreSustanciacion(d.tasaSustanciacion, p);
    valores.push(subscores.sustanciacion);
    pesos.push(p.pesoSustanciacion);
    evaluados.push("sustanciacion");
  } else {
    subscores.sustanciacion = null;
  }

  if (d.diasMedianaCierre != null) {
    subscores.tiempoCierre = scoreTiempo(d.diasMedianaCierre, p);
    valores.push(subscores.tiempoCierre);
    pesos.push(p.pesoTiempo);
    evaluados.push("tiempoCierre");
  } else {
    subscores.tiempoCierre = null;
  }

  if (d.casosUnicos > 0) {
    subscores.represalias = scoreRepresalias(
      d.casosRepresalias / d.casosUnicos,
      p
    );
    valores.push(subscores.represalias);
    pesos.push(p.pesoRepresalias);
    evaluados.push("represalias");
  } else {
    subscores.represalias = null;
  }

  const score = mediaGeometrica(
    valores.map((v) => Math.max(p.piso, v)),
    pesos
  );

  // Inversión a escala de riesgo: alto = malo, que es lo que pinta el gauge.
  const riesgo = (1 - score / 5) * 100;
  const banda = bandaPara(riesgo);

  const subscoresRedondeados = {};
  for (const [k, v] of Object.entries(subscores)) {
    subscoresRedondeados[k] = v == null ? null : redondear(v);
  }

  return {
    score: redondear(score),
    riesgo: redondear(riesgo, 1),
    grade: banda.grade,
    color: banda.color,
    etiqueta: banda.etiqueta,
    tasaCruda: redondear(rCruda),
    tasaAjustada: redondear(rAjustada),
    subscores: subscoresRedondeados,
    componentesEvaluados: evaluados,
    flags: detectarFlags(d, rAjustada, p),
  };
}

// ---------------------------------------------------------------------------
// Diagnóstico — contextualiza, no penaliza el score
// ---------------------------------------------------------------------------

export function detectarFlags(periodo, rAjustada, p = PARAMETROS_DEFAULT) {
  const flags = [];

  if (periodo.empleados < 30) {
    flags.push({
      tipo: "plantilla_minima",
      severidad: "alta",
      mensaje:
        "Plantilla menor a 30: no publicar el score a este nivel. Ruido estadístico y riesgo de identificar al denunciante.",
    });
  }

  if (
    periodo.denunciantesUnicos != null &&
    periodo.denunciantesUnicos > periodo.empleados
  ) {
    flags.push({
      tipo: "denominador_inconsistente",
      severidad: "alta",
      mensaje: `Denunciantes únicos (${periodo.denunciantesUnicos}) supera la plantilla declarada (${periodo.empleados}). Marcar el score como provisional.`,
    });
  }

  if (periodo.reportesBrutos && periodo.casosUnicos) {
    const ratio = periodo.reportesBrutos / periodo.casosUnicos;
    if (ratio >= 3) {
      flags.push({
        tipo: "duplicacion",
        severidad: "media",
        mensaje: `Alta duplicación: ${ratio.toFixed(1)} reportes por caso. Verificar la deduplicación de incidentes.`,
      });
    }
  }

  if (periodo.reportesBrutos && periodo.maxReportesMismoSujeto) {
    const concentracion = periodo.maxReportesMismoSujeto / periodo.reportesBrutos;
    if (concentracion > 0.4) {
      flags.push({
        tipo: "modo_evento",
        severidad: "media",
        mensaje: `${Math.round(concentracion * 100)}% de los reportes apuntan al mismo sujeto o unidad. Pico focalizado, no colapso cultural generalizado.`,
      });
    }
  }

  if (rAjustada < 0.3 * p.benchmark) {
    flags.push({
      tipo: "silencio",
      severidad: "alta",
      mensaje:
        "Volumen muy por debajo del benchmark. Apunta a miedo a represalias, desconocimiento del canal o subutilización, no a ausencia de conducta.",
    });
  }

  if (rAjustada > 5 * p.benchmark) {
    flags.push({
      tipo: "volumen_extremo",
      severidad: "alta",
      mensaje:
        "Volumen muy por encima del benchmark. Revisar si hay conflicto colectivo, uso del canal para quejas de RRHH, o crisis real.",
    });
  }

  if (periodo.casosRepresalias > 0) {
    flags.push({
      tipo: "represalias",
      severidad: "alta",
      mensaje: `${periodo.casosRepresalias} caso(s) de represalia. Escalar de inmediato, independiente del score.`,
    });
  }

  if (periodo.meses < 6) {
    flags.push({
      tipo: "periodo_corto",
      severidad: "baja",
      mensaje: `Período de ${periodo.meses} meses: la anualización amplifica el ruido. Preferir ventana móvil de 12 meses.`,
    });
  }

  return flags;
}

/** Serie para dibujar la curva de volumen en un chart de referencia. */
export function curvaVolumen(p = PARAMETROS_DEFAULT, puntos = 60) {
  const min = Math.log(0.05 * p.benchmark);
  const max = Math.log(40 * p.benchmark);
  return Array.from({ length: puntos }, (_, i) => {
    const tasa = Math.exp(min + ((max - min) * i) / (puntos - 1));
    const s = scoreVolumen(tasa, p);
    return { tasa: redondear(tasa), score: redondear(s), riesgo: redondear((1 - s / 5) * 100, 1) };
  });
}
